import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@context/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@lib/firebase";
import { jsPDF } from "jspdf";

export const SuratPernyataan = () => {
  const { user } = useAuth();
  const [namaLengkap, setNamaLengkap] = useState("");
  const [tanggal, setTanggal] = useState("");
  const [isReady, setIsReady] = useState(false);
  const [lang, setLang] = useState("id");
  const printRef = useRef(null);

  const eventData = {
    namaEvent: "Southeast Asia Trail Running Championship (SEATRC)",
    championshipName: "Southeast Asia Trail Running Championship (SEATRC)",
    lokasi: "Taman Nasional Gunung Gede Pangrango, Cianjur, Jawa Barat",
    tahun: "2026",
    tipeKegiatan: "trail running tingkat internasional",
    organizer: "CAKRAWALA JAKARTA KREATIF",
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      try {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setNamaLengkap(
            data.namaLengkap || user.displayName || "Nama Tidak Ditemukan"
          );
        } else {
          setNamaLengkap(user.displayName || "Nama Tidak Ditemukan");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };

    fetchUserData();

    const today = new Date();
    const options = { day: "2-digit", month: "long", year: "numeric" };
    setTanggal(today.toLocaleDateString("id-ID", options));
  }, [user]);

  useEffect(() => {
    if (printRef.current && namaLengkap && tanggal) {
      setIsReady(true);
    }
  }, [namaLengkap, tanggal]);

  const formatDateForLang = (dateObj, language) => {
    if (!dateObj) return "";
    const opts = { day: "2-digit", month: "long", year: "numeric" };
    return dateObj.toLocaleDateString(
      language === "en" ? "en-US" : "id-ID",
      opts
    );
  };

  const buildContent = (language) => {
    const nameLabel = language === "en" ? "Name" : "Nama";
    const dateLabel = language === "en" ? "Date" : "Tanggal";

    if (language === "en") {
      const paragraphs = [
        `I hereby declare that I voluntarily and consciously register myself as a participant in the ${eventData.namaEvent}, which will be held at ${eventData.lokasi}. `,

        `I acknowledge that this event is physically and mentally challenging and involves inherent risks, including but not limited to extreme weather conditions, difficult terrain, significant altitude changes, and other potential health hazards. I confirm that I have adequately prepared myself and am in good health to safely participate in this event. `,

        `I acknowledge that the event organizers, volunteers, sponsors, and all related parties cannot be held legally liable for injuries, accidents, losses, or other emergencies that may occur during my participation. I accept full responsibility for my personal safety and health throughout the event and hereby release all parties from any and all legal claims arising from or related to my participation. `,

        `I commit to the following:`,
        [
          "Adhering to all rules and instructions provided by the organizers and authorized officials.",
          "Maintaining sportsmanship, safety, and order throughout the duration of the event.",
          "Refraining from any actions that may harm myself, other participants, organizers, or the surrounding environment.",
          "Respecting the natural environment and preserving the integrity of Mount Gede Pangrango National Park.",
        ],

        `I grant full and irrevocable permission to the organizers to use any and all documentation, including photographs, videos, or other media that include my image or likeness, for purposes of publication, promotion, social media, and event documentation, without requiring my further consent. `,

        `I confirm that all personal information provided during the registration process is accurate and truthful, and I declare that I have no medical conditions that would prevent my safe participation in this event. `,

        `By signing this declaration, I confirm that I have carefully read, fully understood, and voluntarily agree to all terms and conditions stated herein without any coercion or pressure from any party. This declaration represents my personal commitment and acceptance of responsibility as a participant in the ${eventData.namaEvent}. `,
      ];
      return { title: "DECLARATION LETTER", paragraphs, nameLabel, dateLabel };
    } else {
      const paragraphs = [
        `Dengan ini saya menyatakan bahwa saya secara sukarela dan sadar mendaftarkan diri sebagai peserta dalam kegiatan ${eventData.namaEvent},  yang akan dilaksanakan di ${eventData.lokasi}. `,

        `Saya memahami bahwa kegiatan ini menantang secara fisik dan mental, serta memiliki potensi risiko, seperti kondisi cuaca ekstrem, kontur medan yang berat, ketinggian yang signifikan, dan gangguan kesehatan lainnya. Oleh karena itu, saya menyatakan bahwa saya telah mempersiapkan diri secara optimal untuk mengikuti kegiatan ini dan dalam kondisi kesehatan yang baik.`,

        `Saya menyadari bahwa panitia penyelenggara, relawan, sponsor, maupun pihak-pihak terkait lainnya tidak bertanggung jawab secara hukum atas risiko cedera, kecelakaan, kehilangan, atau kejadian darurat lainnya yang mungkin saya alami selama mengikuti rangkaian kegiatan. Seluruh tanggung jawab atas keselamatan dan kesehatan pribadi selama kegiatan merupakan tanggung jawab saya sendiri, dan saya melepaskan semua pihak dari segala bentuk tuntutan hukum atas kejadian yang mungkin timbul selama atau akibat keikutsertaan saya dalam kegiatan ini.`,

        `Saya berkomitmen untuk:`,
        [
          "Mengikuti seluruh peraturan dan arahan dari panitia dan pihak berwenang.",
          "Menjaga sportivitas, keamanan, dan ketertiban selama kegiatan berlangsung.",
          "Tidak melakukan tindakan yang merugikan diri sendiri, peserta lain, panitia, maupun lingkungan sekitar.",
          "Menghormati lingkungan alami dan menjaga kelestarian Taman Nasional Gunung Gede Pangrango.",
        ],

        `Saya juga memberikan izin penuh kepada panitia untuk menggunakan dokumentasi (foto, video, atau lainnya) yang memuat identitas atau citra saya untuk keperluan publikasi, promosi, media sosial, dan dokumentasi kegiatan tanpa memerlukan izin tambahan. `,

        `Saya menegaskan bahwa data pribadi saya yang diberikan dalam pendaftaran adalah akurat dan benar, serta saya tidak memiliki kondisi kesehatan yang menghalangi partisipasi dalam kegiatan ini.`,

        `Dengan ini, saya menyatakan telah membaca, memahami, dan menyetujui seluruh isi pernyataan ini dengan sadar, sukarela, dan tanpa tekanan dari pihak mana pun. Pernyataan ini saya buat sebagai bentuk komitmen dan tanggung jawab saya sebagai peserta dalam ${eventData.namaEvent}.`,
      ];
      return { title: "SURAT PERNYATAAN", paragraphs, nameLabel, dateLabel };
    }
  };

  const generatePDF = async () => {
    if (!isReady || !printRef.current) {
      const message =
        lang === "en"
          ? "Document is not ready. Please wait a moment."
          : "Dokumen belum siap untuk diunduh. Silakan tunggu sebentar.";
      alert(message);
      return;
    }

    const content = buildContent(lang);
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const marginLeft = 20;
    const marginTop = 20;
    const lineHeight = 7;
    const bulletIndent = 5;
    let yPosition = marginTop;

    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text(content.title, 105, yPosition, { align: "center" });
    yPosition += lineHeight * 2;

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");

    const today = new Date();
    const dateStr = formatDateForLang(today, lang);
    pdf.text(`${content.nameLabel}: ${namaLengkap}`, marginLeft, yPosition);
    yPosition += lineHeight;
    pdf.text(`${content.dateLabel}: ${dateStr}`, marginLeft, yPosition);
    yPosition += lineHeight * 2;

    content.paragraphs.forEach((item) => {
      if (Array.isArray(item)) {
        item.forEach((bullet) => {
          if (yPosition + lineHeight > 277) {
            pdf.addPage();
            yPosition = marginTop;
          }
          pdf.text(`• ${bullet}`, marginLeft + bulletIndent, yPosition);
          yPosition += lineHeight;
        });
      } else {
        const lines = pdf.splitTextToSize(item, 170);
        lines.forEach((line) => {
          if (yPosition + lineHeight > 277) {
            pdf.addPage();
            yPosition = marginTop;
          }
          pdf.text(line, marginLeft, yPosition);
          yPosition += lineHeight;
        });
      }
      yPosition += lineHeight;
    });

    if (yPosition + lineHeight * 3 > 277) {
      pdf.addPage();
      yPosition = marginTop;
    }
    const honord = lang === "en" ? "Sincerely," : "Hormat saya,";
    pdf.text(honord, 170, yPosition, { align: "right" });
    yPosition += lineHeight * 2;
    pdf.setFont("helvetica", "bold");
    pdf.text(namaLengkap, 170, yPosition, { align: "right" });

    const safeName = namaLengkap.replace(/\s+/g, "_");
    pdf.save(`Surat_Pernyataan_JOTR_2026_${safeName}_${lang}.pdf`);
  };

  if (!namaLengkap || !tanggal) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center text-gray-600 text-lg font-semibold p-6">
          {lang === "en" ? "Loading data..." : "Memuat data..."}
        </div>
      </div>
    );
  }

  const content = buildContent(lang);
  const today = new Date();
  const dateStr = formatDateForLang(today, lang);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white py-20 px-4">
      <div className="w-full max-w-[21cm]">
        <div className="flex justify-end mb-4">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              onClick={() => setLang("id")}
              className={`px-4 py-1 text-sm font-medium transition ${
                lang === "id"
                  ? "bg-red-900 text-white rounded-l-md"
                  : "bg-white text-red-900 border border-red-200 rounded-l-md hover:bg-red-50"
              }`}
            >
              Bahasa (ID)
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-4 py-1 text-sm font-medium transition ${
                lang === "en"
                  ? "bg-red-900 text-white rounded-r-md"
                  : "bg-white text-red-900 border border-red-200 rounded-r-md hover:bg-red-50"
              }`}
            >
              English
            </button>
          </div>
        </div>

        <div
          ref={printRef}
          className="bg-white p-4 sm:p-8 shadow-lg rounded-xl text-justify border border-gray-200 w-full mx-auto"
        >
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-6 text-gray-800">
            {content.title}
          </h2>

          <p className="mb-4 text-gray-700 text-sm sm:text-base">
            <strong className="text-gray-900">{content.nameLabel}:</strong>{" "}
            {namaLengkap}
          </p>
          <p className="mb-6 text-gray-700 text-sm sm:text-base">
            <strong className="text-gray-900">{content.dateLabel}:</strong>{" "}
            {dateStr}
          </p>

          {content.paragraphs.map((item, idx) => {
            if (Array.isArray(item)) {
              return (
                <ul
                  key={idx}
                  className="mb-4 text-gray-700 leading-relaxed text-sm sm:text-base list-disc list-inside"
                >
                  {item.map((li, i) => (
                    <li key={i}>{li}</li>
                  ))}
                </ul>
              );
            } else {
              return (
                <p
                  key={idx}
                  className="mb-4 text-gray-700 leading-relaxed text-sm sm:text-base"
                >
                  {item}
                </p>
              );
            }
          })}

          <p className="mt-6 text-left text-gray-700 text-sm sm:text-base">
            {lang === "en" ? "Sincerely," : "Hormat saya,"}
          </p>
          <p className="mt-4 text-left font-bold text-gray-900 text-sm sm:text-base">
            {namaLengkap}
          </p>
        </div>

        <div className="flex justify-center mt-6">
          <button
            onClick={generatePDF}
            disabled={!isReady}
            className={`px-6 py-2 rounded-xl font-semibold text-white transition-colors duration-200 ${
              isReady
                ? "bg-red-900 hover:bg-red-700"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            {lang === "en" ? "Download PDF" : "Unduh PDF"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuratPernyataan;
